import { useEffect, useState } from "react";
import { api, formatBRL, formatDateBR } from "../../lib/api";
import { Plus, CheckCircle, Trash2, Zap, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ExportButtons from "../../components/ExportButtons";

export default function Financeiro() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", description: "", amount: 0, due_date: "", method: "pix" });
  const [showForm, setShowForm] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(null);

  const load = () => Promise.all([
    api.get("/invoices").then(r => setItems(r.data)),
    api.get("/clients").then(r => setClients(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", { ...form, amount: +form.amount });
      toast.success("Fatura criada");
      setShowForm(false);
      setForm({ client_id: "", description: "", amount: 0, due_date: "", method: "pix" });
      load();
    } catch { toast.error("Erro"); }
  };

  const pay = async (id) => { await api.patch(`/invoices/${id}/pay`); toast.success("Marcada como paga"); load(); };
  const remove = async (id) => { if (!confirm("Remover fatura?")) return; await api.delete(`/invoices/${id}`); load(); };

  const generateCharge = async (inv) => {
    try {
      const { data } = await api.post("/payments/charge", { invoice_id: inv.id, method: inv.method });
      toast.success("Cobrança Asaas gerada");
      setChargeOpen({ invoice: { ...inv, ...data.charge }, charge: data.charge });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar cobrança");
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copiado!");
  };

  const totals = items.reduce((acc, i) => {
    if (i.status === "paid") acc.paid += i.amount;
    else if (i.status === "overdue") acc.overdue += i.amount;
    else acc.pending += i.amount;
    return acc;
  }, { paid: 0, pending: 0, overdue: 0 });

  return (
    <div data-testid="admin-financeiro">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Financeiro</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Faturas & Cobranças</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <ExportButtons resource="financeiro" />
          <button onClick={() => setShowForm(!showForm)} className="flex-1 sm:flex-none bg-[#14213D] text-white px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="new-invoice-btn">
            <Plus size={16} /> Nova Fatura
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-emerald-200 rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">A Receber</div>
          <div className="font-display text-2xl text-emerald-700">{formatBRL(totals.pending)}</div>
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1">Vencido</div>
          <div className="font-display text-2xl text-red-700">{formatBRL(totals.overdue)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Já Recebido</div>
          <div className="font-display text-2xl text-[#14213D]">{formatBRL(totals.paid)}</div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white border border-gray-200 rounded-lg p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="invoice-form">
          <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
            <option value="">Selecione o cliente *</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
          </select>
          <input required placeholder="Descrição *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input required type="number" step="0.01" placeholder="Valor (R$) *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
            <option value="pix">PIX</option>
            <option value="boleto">Boleto</option>
            <option value="cartao">Cartão</option>
          </select>
          <button type="submit" className="bg-[#D4AF37] text-[#14213D] font-semibold rounded text-sm" data-testid="save-invoice">Gerar Fatura</button>
        </form>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Cliente</th>
              <th className="px-5 py-3">Descrição</th>
              <th className="px-5 py-3">Vencimento</th>
              <th className="px-5 py-3">Valor</th>
              <th className="px-5 py-3">Método</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Asaas</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-400">Nenhuma fatura</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`invoice-${i.id}`}>
                <td className="px-5 py-3 font-semibold text-[#14213D]">{i.client_name}</td>
                <td className="px-5 py-3 text-gray-700">{i.description}</td>
                <td className="px-5 py-3 text-gray-600">{formatDateBR(i.due_date)}</td>
                <td className="px-5 py-3 font-semibold text-[#14213D]">{formatBRL(i.amount)}</td>
                <td className="px-5 py-3 text-gray-600 uppercase text-xs">{i.method}</td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                    i.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                    i.status === "overdue" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                  }`}>{i.status === "paid" ? "paga" : i.status === "overdue" ? "vencida" : "pendente"}</span>
                </td>
                <td className="px-5 py-3">
                  {i.asaas_payment_id ? (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                      {i.asaas_status || "Gerada"}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-5 py-3 flex gap-1 items-center">
                  {!i.asaas_payment_id && i.status !== "paid" && (
                    <button onClick={() => generateCharge(i)} className="text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded p-1.5 border border-[#D4AF37]/40" title="Gerar cobrança Asaas" data-testid={`charge-${i.id}`}>
                      <Zap size={14} />
                    </button>
                  )}
                  {i.asaas_payment_id && (
                    <button onClick={() => setChargeOpen({ invoice: i, charge: { id: i.asaas_payment_id, invoiceUrl: i.asaas_invoice_url, pixCopyPaste: i.asaas_pix_copy_paste, bankSlipUrl: i.asaas_bank_slip_url } })} className="text-blue-600 hover:bg-blue-50 rounded p-1.5" title="Ver cobrança">
                      <ExternalLink size={14} />
                    </button>
                  )}
                  {i.status !== "paid" && (
                    <button onClick={() => pay(i.id)} className="text-emerald-600 hover:bg-emerald-50 rounded p-1.5" title="Marcar paga" data-testid={`pay-${i.id}`}>
                      <CheckCircle size={15} />
                    </button>
                  )}
                  <button onClick={() => remove(i.id)} className="text-red-600 hover:bg-red-50 rounded p-1.5" data-testid={`del-invoice-${i.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {chargeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="charge-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setChargeOpen(null)} />
          <div className="relative bg-white rounded-lg w-full max-w-lg p-6 shadow-2xl">
            <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Cobrança gerada</div>
            <h3 className="font-display text-xl text-[#14213D] mb-1">Asaas Payment</h3>
            <div className="text-xs text-gray-500 mb-4 font-mono">{chargeOpen.charge?.id}</div>
            <div className="space-y-3 text-sm">
              {chargeOpen.charge?.invoiceUrl && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Link de pagamento</div>
                  <a href={chargeOpen.charge.invoiceUrl} target="_blank" rel="noreferrer" className="text-[#14213D] hover:text-[#D4AF37] underline break-all text-xs">{chargeOpen.charge.invoiceUrl}</a>
                </div>
              )}
              {chargeOpen.charge?.bankSlipUrl && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Boleto (PDF)</div>
                  <a href={chargeOpen.charge.bankSlipUrl} target="_blank" rel="noreferrer" className="text-[#14213D] hover:text-[#D4AF37] underline text-xs">Abrir boleto</a>
                </div>
              )}
              {chargeOpen.charge?.pixCopyPaste && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center justify-between">
                    PIX Copia e Cola
                    <button onClick={() => copy(chargeOpen.charge.pixCopyPaste)} className="text-[#14213D] flex items-center gap-1 normal-case font-normal hover:text-[#D4AF37]"><Copy size={11} /> Copiar</button>
                  </div>
                  <div className="font-mono text-[10px] bg-gray-50 border border-gray-200 rounded p-2 break-all max-h-24 overflow-y-auto">{chargeOpen.charge.pixCopyPaste}</div>
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between">
              <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                {chargeOpen.charge?.mocked ? "Asaas em modo MOCK" : "Asaas live"}
              </div>
              <button onClick={() => setChargeOpen(null)} className="bg-[#14213D] text-white px-5 py-2 rounded text-sm font-semibold">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
